<template>
  <div class="login-container">
    <div class="login-card">
      <h1 class="login-title">文件中转站登录</h1>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="authCode" class="form-label">
            <span class="required">*</span>code
          </label>
          <input id="authCode" v-model="authCode" type="text" class="form-input"
            :class="{ 'error': fieldErrors.authCode?.length > 0 }" placeholder="请输入code"
            @input="validateField('authCode', authCode)" @blur="validateField('authCode', authCode)" />
          <div v-if="fieldErrors.authCode?.length > 0" class="error-message">
            {{ fieldErrors.authCode[0] }}
          </div>
        </div>

        <button type="submit" class="login-btn" :disabled="loading">
          {{ loading ? '登录中...' : '登录' }}
        </button>
      </form>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { showDialog } from '../Dialog/index.js'
import request from '../api/request.js'
import { createLogger } from '../util/logger.js'
import { isWeChatBrowser, getQueryParam, getQueryParamFromRouteQuery } from '../util/browser.js'
import uploadStorage from '../util/uploadStorage.js'

const logger = createLogger('Home')

const router = useRouter()
const route = useRoute()
const authCode = ref('')
const loading = ref(false)
const fieldErrors = ref({})


// 实时验证单个字段
const validateField = (fieldName, value) => {
  const errors = []
  const trimmedValue = value?.trim() || ''

  if (fieldName === 'authCode') {
    rules.authCode.forEach(rule => {
      if (rule.required && !trimmedValue) {
        errors.push(rule.message)
      } else if (rule.min && trimmedValue.length < rule.min) {
        errors.push(rule.message)
      } else if (rule.pattern && trimmedValue && !rule.pattern.test(trimmedValue)) {
        errors.push(rule.message)
      }
    })
  }

  fieldErrors.value[fieldName] = errors
  return errors.length === 0
}


// 表单验证规则
const rules = reactive({
  authCode: [
    { required: true, message: 'code不能为空', trigger: 'blur' },
    { min: 6, message: 'code异常', trigger: 'blur' },
  ]
})


// 组件挂载时检查URL参数
onMounted(() => {
  logger.info('onMounted:', route.query)
  // 检查 URL 中是否有 code（search / hash / route.query 多源兼容，避免部分浏览器无法识别）
  const codeParam = getQueryParam('code') || getQueryParamFromRouteQuery(route.query, 'code')
  if (codeParam) {
    authCode.value = codeParam
    // 将code存储到localStorage，用于刷新时恢复
    localStorage.setItem('lastAuthCode', codeParam)
    // 自动验证输入的授权码
    validateField('authCode', authCode.value)
  }
})

// 验证表单
const validateForm = () => {
  const errors = []
  const value = authCode.value?.trim() || ''

  // 应用验证规则
  rules.authCode.forEach(rule => {
    if (rule.required && !value) {
      errors.push(rule.message)
    } else if (rule.min && value.length < rule.min) {
      errors.push(rule.message)
    }
  })

  return {
    isValid: errors.length === 0,
    errors
  }
}

const getListData = async (uploadParams) => {
  const result = await request.getListData(uploadParams)
  if (result.success) {
    // 处理成功情况，跳转到上传页面
    router.push('/upload')
  } else {
    // 处理失败情况
    showDialog({
      title: '验证失败',
      message: result.error || '未知错误',
      type: 'error'
    })
  }
}

const handleLogin = async () => {
  // 检测是否是微信浏览器
  if (isWeChatBrowser()) {
    showDialog({
      title: '不支持微信浏览器',
      message: '为了更好的使用体验，请在浏览器中打开此页面。\n\n请点击右上角菜单，选择"在浏览器中打开"。',
      type: 'warning',
      confirmText: '我知道了'
    })
    return
  }

  // 验证表单
  const validation = validateForm()
  if (!validation.isValid && validation.errors.length > 0) {
    showDialog({
      title: '验证失败',
      message: validation.errors[0],
      type: 'error'
    })
    return
  }

  loading.value = true


  // 使用工具函数进行登录
  const loginResult = await request.autoLogin(authCode.value)
  if (loginResult.success) {
    // 处理成功情况
    localStorage.setItem('lastAuthCode', authCode.value)

    const uploadParams = loginResult.data
    
    // 如果有parentFileId，则调用getListData获取文件列表
    if (uploadParams.accessToken) {
      await getListData(uploadParams)
    } else {
      // 将参数存入 sessionStorage（按 code 区分），供 Upload 页面使用
      sessionStorage.setItem(uploadStorage.getParamsKey(), JSON.stringify(uploadParams))
      // 跳转到上传页面
      router.push('/upload')
    }
  } else {
    // 处理失败情况
    logger.error('登录失败:', loginResult.error)
    showDialog({
      title: '登录失败',
      message: loginResult.error || '未知错误',
      type: 'error'
    })
  }
  loading.value = false
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 1rem;
}

.login-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
}

.login-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 2rem 0;
  text-align: center;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #555;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.required {
  color: #e74c3c;
  font-weight: bold;
}

.form-input {
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
}

.form-input.error {
  border-color: #e74c3c;
  box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.2);
}

.error-message {
  color: #e74c3c;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.login-btn {
  background: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.login-btn:hover:not(:disabled) {
  background: #2980b9;
}

.login-btn:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .login-card {
    padding: 1.5rem;
    margin: 0.5rem;
  }

  .login-title {
    font-size: 1.25rem;
  }
}
</style>